/**
 * ONE address-list parser for every compose surface (useComposeState,
 * RecipientFields, ChipInput, optimistic thread messages). The previous four
 * copies all split on a bare /[,;]/ — a display name containing a comma
 * ("Doe, John <jd@x.com>") was torn into two chips, one of them invalid.
 * Splitting here is quote- and angle-bracket-aware.
 */

/** Split a comma/semicolon-separated address list, honoring quotes and <>. */
export function splitAddressList(value: string): string[] {
  if (!value) {
    return [];
  }
  const parts: string[] = [];
  let current = '';
  let inQuotes = false;
  let inAngle = false;
  for (const ch of value) {
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === '<' && !inQuotes) {
      inAngle = true;
    } else if (ch === '>' && !inQuotes) {
      inAngle = false;
    }
    if ((ch === ',' || ch === ';') && !inQuotes && !inAngle) {
      const trimmed = current.trim();
      if (trimmed) {
        parts.push(trimmed);
      }
      current = '';
      continue;
    }
    current += ch;
  }
  const tail = current.trim();
  if (tail) {
    parts.push(tail);
  }
  return parts;
}

/** Extract just the email from `Name <email>` / `"Name" <email>` / `email`. */
export function extractEmailAddress(chip: string): string {
  const match = chip.match(/<([^>]+)>\s*$/);
  return (match ? match[1] : chip).trim();
}

/** Extract the display name (unquoted) from a chip, or null. */
export function extractDisplayName(chip: string): string | null {
  const match = chip.match(/^(.*?)\s*<[^>]+>\s*$/);
  if (!match) {
    return null;
  }
  const raw = match[1].trim();
  if (!raw) {
    return null;
  }
  // Strip RFC quoting
  if (raw.startsWith('"') && raw.endsWith('"')) {
    return raw.slice(1, -1).replace(/\\"/g, '"');
  }
  return raw;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;

/** Basic RFC 5321/5322 email format validation (on the address part). */
export function isValidAddressChip(chip: string): boolean {
  return EMAIL_REGEX.test(extractEmailAddress(chip).toLowerCase());
}

/**
 * Format a chip from name+email, RFC-quoting display names that contain
 * specials (comma, semicolon, angle brackets, quotes) so the round-trip
 * through splitAddressList is loss-free.
 */
export function formatAddressChip(name: string | null | undefined, email: string): string {
  const trimmed = name?.trim();
  if (!trimmed || trimmed === email) {
    return email;
  }
  if (/[,;<>"\\]/.test(trimmed)) {
    return `"${trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}" <${email}>`;
  }
  return `${trimmed} <${email}>`;
}

export function joinAddressList(chips: string[]): string {
  return chips.join(', ');
}
